import {
  AutocompleteValue,
  CircularProgress,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  TextField,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { getComputedLabel } from 'utils/label-required-option';

type CustomProps = {
  label: string;
  hiddenLabel?: boolean;
  dark?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
};

type ControllerProp =
  | { value?: unknown; controller: UseControllerProps<any> }
  | { value?: unknown; controller?: UseControllerProps<any> };

type Props<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = ControllerProp & CustomProps & Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>;

function Autocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  label,
  hiddenLabel,
  error,
  helperText,
  dark = false,
  controller,
  options,
  multiple,
  limitTags = 1,
  size,
  fullWidth = false,
  loading = false,
  value,
  disableCloseOnSelect = true,
  disabled = false,
  onFocus,
  onChange,
  getOptionLabel,
  renderOption,
  isOptionEqualToValue,
  startAdornment,
  endAdornment,
  ...props
}: Props<T, Multiple, DisableClearable, FreeSolo>) {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';

  /**
   * Provides a default implementation for isOptionEqualToValue that compares the JSON stringified versions of the option and value. This is a simple deep equality check, but it may not be the most efficient for large objects. Consumers can provide their own implementation if needed.
   */
  const defaultIsOptionEqualToValue = (option: T, value: T) => JSON.stringify(option) === JSON.stringify(value);

  const isOptionEqual = isOptionEqualToValue ?? defaultIsOptionEqualToValue;

  return controller ? (
    <Controller
      {...controller}
      render={({ field, fieldState }) => {
        const computedLabel = getComputedLabel(label, controller);
        const currentHelperText = fieldState.error?.message ?? helperText;

        return (
          <MuiAutocomplete<T, Multiple, DisableClearable, FreeSolo>
            {...props}
            fullWidth={fullWidth}
            size={size}
            className={backgroundClass}
            disableCloseOnSelect={disableCloseOnSelect}
            popupIcon={loading ? <CircularProgress size={20} /> : <ChevronDown />}
            multiple={multiple}
            limitTags={multiple && limitTags ? limitTags : -1}
            options={options}
            value={
              (!!field.value ? field.value : !!multiple ? [] : null) as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
            }
            defaultValue={
              (!!field.value ? field.value : !!multiple ? [] : null) as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
            }
            disabled={disabled}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqual}
            renderOption={renderOption}
            onFocus={onFocus}
            onChange={(event, data, reason, details) => {
              field.onChange(data);
              if (onChange) onChange(event, data, reason, details);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                  },
                  input: {
                    ...params.InputProps,
                    className: backgroundClass,
                    error: error,
                    sx: {
                      flexWrap: 'wrap',
                    },
                    startAdornment: (startAdornment || params.InputProps.startAdornment) && (
                      <>
                        {startAdornment}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (endAdornment || params.InputProps.endAdornment) && (
                      <>
                        {endAdornment}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: {
                    className: backgroundClass,
                    error: error,
                  },
                  formHelperText: {
                    className: backgroundClass,
                    error: error,
                    component: 'div',
                  },
                }}
                className={backgroundClass}
                label={hiddenLabel ? '' : computedLabel}
                error={error ?? fieldState.invalid}
                helperText={currentHelperText}
              />
            )}
          />
        );
      }}
    />
  ) : (
    <MuiAutocomplete<T, Multiple, DisableClearable, FreeSolo>
      {...props}
      size={size}
      popupIcon={loading ? <CircularProgress size={20} /> : <ChevronDown />}
      multiple={multiple}
      disableCloseOnSelect={disableCloseOnSelect}
      limitTags={multiple && limitTags ? limitTags : -1}
      options={options}
      value={(!!value ? value : !!multiple ? [] : null) as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>}
      defaultValue={(!!value ? value : !!multiple ? [] : null) as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqual}
      renderOption={renderOption}
      onFocus={onFocus}
      onChange={onChange}
      slotProps={{
        chip: {
          className: backgroundClass,
          variant: 'filled',
        },
      }}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          className={backgroundClass}
          label={hiddenLabel ? '' : label}
          helperText={helperText}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              autoComplete: 'new-password', // disable autocomplete and autofill
            },
            input: {
              ...params.InputProps,
              className: backgroundClass,
              error: error,
              sx: {
                flexWrap: 'wrap',
              },
              startAdornment: (startAdornment || params.InputProps.startAdornment) && (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (endAdornment || params.InputProps.endAdornment) && (
                <>
                  {endAdornment}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            inputLabel: {
              className: backgroundClass,
              error: error,
            },
            formHelperText: {
              className: backgroundClass,
              error: error,
              component: 'div',
            },
          }}
        />
      )}
    />
  );
}

export default Autocomplete;
